import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Plus, X, Save, CalendarRange, Trash2, Loader2, Wand2, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSubjects } from "@/components/QuickAddHomework";
import { subjectStyle, fmtDue, ukToday, daysUntil, plusDays } from "@/lib/subjects";
import { W } from "@/lib/edition";

const field = "h-10 rounded-xl border border-slate-200 bg-surface-deep/60 px-3 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400";
const STATUS = { above: ["On target", "text-emerald-400", "bg-emerald-400"], close: ["Close", "text-gold-300", "bg-gold-400"], below: ["Below target", "text-rose-600", "bg-brand"] };

const TargetRow = ({ t }) => {
  const st = STATUS[t.status];
  return (
    <li data-testid={`target-row-${t.subject.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl border border-slate-200 bg-surface-2 p-4">
      <div className="flex items-center gap-2">
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${subjectStyle(t.subject).chip}`}>{t.subject}</span>
        {t.grade && <span className="text-xs text-slate-500">target grade <b className="text-white">{t.grade}</b></span>}
        <span className={`ml-auto text-xs font-bold ${st ? st[1] : "text-slate-500"}`}>{st ? st[0] : t.average == null ? "No results yet" : "Set a target %"}</span>
      </div>
      <div className="relative mt-3 h-3 rounded-full bg-slate-100">
        {t.average != null && <div className={`absolute inset-y-0 left-0 rounded-full ${st ? st[2] : "bg-navy-500"}`} style={{ width: `${t.average}%` }} />}
        {t.percent != null && <div className="absolute -top-1 -bottom-1 w-0.5 bg-white" style={{ left: `${t.percent}%` }} title={`Target ${t.percent}%`} />}
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {t.average != null ? <>Average <b className="text-white">{t.average}%</b> over {t.results.length} test{t.results.length === 1 ? "" : "s"} · latest {t.latest}%</> : "Add scores to your tests on the Homework page."}
        {t.percent != null && <> · target {t.percent}%</>}
      </p>
    </li>
  );
};

const Targets = () => {
  const subs = useSubjects();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState([]);
  const [editing, setEditing] = useState(false);
  const load = () => api.get("/targets").then((r) => { const l = Array.isArray(r.data) ? r.data : []; setList(l); setEdit(l.filter((t) => t.grade || t.percent != null).map(({ subject, grade, percent }) => ({ subject, grade, percent }))); }).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/targets", { targets: edit.filter((t) => t.subject.trim()).map((t) => ({ ...t, percent: t.percent === "" || t.percent == null ? null : Number(t.percent) })) }); toast.success("Targets saved"); setEditing(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const set = (i, k, v) => setEdit((e) => e.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <section data-testid="target-grades-chart" className="card p-5 sm:p-6">
      <div className="flex items-end justify-between gap-3">
        <div><p className="eyebrow flex items-center gap-2"><Target className="w-4 h-4" />Target grades</p><h2 className="mt-1 font-display text-3xl text-white">Targets vs results</h2></div>
        <button data-testid="targets-edit" onClick={() => setEditing(!editing)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:text-white">{editing ? "Close" : "Edit targets"}</button>
      </div>
      {editing && (
        <div className="mt-4 space-y-2 animate-rise">
          {edit.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input data-testid={`target-subject-${i}`} list="target-subjects" value={t.subject} onChange={(e) => set(i, "subject", e.target.value)} placeholder="Subject" className={`${field} flex-1 min-w-[8rem]`} />
              <input data-testid={`target-grade-${i}`} value={t.grade} onChange={(e) => set(i, "grade", e.target.value.slice(0, 8))} placeholder="Grade (e.g. 7)" className={`${field} w-28`} />
              <input data-testid={`target-percent-${i}`} type="number" min={0} max={100} value={t.percent ?? ""} onChange={(e) => set(i, "percent", e.target.value)} placeholder="Target %" className={`${field} w-28`} />
              <button data-testid={`target-remove-${i}`} onClick={() => setEdit((e) => e.filter((_, j) => j !== i))} aria-label="Remove" className="grid place-items-center w-10 h-10 rounded-xl border border-slate-200 text-slate-500 hover:bg-rose-600 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <datalist id="target-subjects">{subs.map((s) => <option key={s.subject} value={s.subject} />)}</datalist>
          <div className="flex gap-2 pt-1">
            <button data-testid="target-add" onClick={() => setEdit((e) => [...e, { subject: "", grade: "", percent: "" }])} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"><Plus className="w-4 h-4" />Add subject</button>
            <button data-testid="targets-save" onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-gold-400 px-4 py-2 text-sm font-bold text-black hover:bg-gold-300"><Save className="w-4 h-4" />Save</button>
          </div>
        </div>
      )}
      {list?.length === 0 && !editing && <p className="mt-4 text-sm text-slate-500">Set a target for each subject, then add your test scores to see how you're doing.</p>}
      <ul className="mt-4 grid gap-2.5 md:grid-cols-2">{(list ?? []).map((t) => <TargetRow key={t.subject} t={t} />)}</ul>
    </section>
  );
};

const Exams = () => {
  const subs = useSubjects();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ subject: "", paper: "", date: plusDays(14) });
  const [spd, setSpd] = useState(2);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/exams").then((r) => setD(r.data ?? { exams: [], plan: [] })).catch(() => setD({ exams: [], plan: [] }));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/exams", f); setF({ ...f, paper: "" }); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const plan = async () => {
    setBusy(true);
    try { await api.post("/exams/plan", { sessions_per_day: spd, minutes: 40 }); toast.success("Revision schedule made"); load(); } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const del = async (id) => { await api.delete(`/exams/${id}`).catch(() => {}); load(); };
  const tick = async (day, i, done) => { await api.put(`/exams/plan/${day}/${i}`, { done }).catch(() => {}); load(); };
  const exams = Array.isArray(d?.exams) ? d.exams : [];
  const days = (Array.isArray(d?.plan) ? d.plan : []).filter((x) => x.date >= ukToday()).slice(0, 21);
  return (
    <section data-testid="exam-planner" className="card p-5 sm:p-6">
      <p className="eyebrow flex items-center gap-2"><CalendarRange className="w-4 h-4" />Exam season planner</p>
      <h2 className="mt-1 font-display text-3xl text-white">Mocks and GCSEs</h2>
      <form onSubmit={add} className="mt-4 flex flex-wrap gap-2">
        <input data-testid="exam-subject-input" list="exam-subjects" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" className={`${field} flex-1 min-w-[8rem]`} />
        <input data-testid="exam-paper-input" value={f.paper} onChange={(e) => setF({ ...f, paper: e.target.value })} placeholder="Paper (e.g. Paper 1)" className={`${field} flex-1 min-w-[8rem]`} />
        <input data-testid="exam-date-input" type="date" min={plusDays(1)} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} />
        <button data-testid="exam-add" disabled={!f.subject.trim() || !f.date} className="inline-flex items-center gap-1.5 h-10 rounded-xl bg-gold-400 px-4 text-sm font-bold text-black disabled:opacity-40"><Plus className="w-4 h-4" />Add exam</button>
        <datalist id="exam-subjects">{subs.map((s) => <option key={s.subject} value={s.subject} />)}</datalist>
      </form>
      <ul className="mt-4 space-y-1.5">
        {exams.map((e) => (
          <li key={e.exam_id} data-testid={`exam-row-${e.exam_id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-surface-2 px-3 py-2 text-sm">
            <span className="w-1.5 h-8 rounded-full" style={{ backgroundColor: subjectStyle(e.subject).dot }} />
            <span className="flex-1 min-w-0"><span className="font-semibold text-white">{e.subject}</span>{e.paper && <span className="text-slate-500"> · {e.paper}</span>}</span>
            <span className="font-mono text-xs text-slate-600">{fmtDue(e.date)} · {daysUntil(e.date) >= 0 ? `${daysUntil(e.date)}d` : "done"}</span>
            <button data-testid={`exam-delete-${e.exam_id}`} onClick={() => del(e.exam_id)} aria-label="Delete exam" className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-rose-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
      {exams.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="text-sm text-slate-600">Sessions a day
            <select data-testid="exam-spd" value={spd} onChange={(e) => setSpd(Number(e.target.value))} className="ml-2 h-10 rounded-xl border border-slate-200 bg-surface px-2 text-sm text-white">{[1, 2, 3, 4].map((n) => <option key={n}>{n}</option>)}</select>
          </label>
          <button data-testid="exam-make-plan" onClick={plan} disabled={busy} className="inline-flex items-center gap-2 h-10 rounded-full bg-brand px-5 text-sm font-bold text-white hover:bg-brand-dark shadow-red">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}{days.length ? "Rebuild schedule" : "Make my schedule"}</button>
        </div>
      )}
      {days.length > 0 && (
        <ol data-testid="exam-plan" className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {days.map((day) => (
            <li key={day.date} className={`rounded-2xl border p-3 ${day.date === ukToday() ? "border-gold-400 bg-gold-400/5" : "border-slate-200 bg-surface-2"}`}>
              <p className="text-xs font-bold text-slate-600">{day.date === ukToday() ? "Today" : fmtDue(day.date)}</p>
              <ul className="mt-2 space-y-1.5">
                {day.sessions.map((s, i) => (
                  <li key={i}><button data-testid={`exam-session-${day.date}-${i}`} onClick={() => tick(day.date, i, !s.done)} className="w-full flex items-center gap-2 text-left text-sm">
                    <span className={`grid place-items-center w-5 h-5 shrink-0 rounded border ${s.done ? "bg-gold-400 border-gold-400 text-black" : "border-slate-300"}`}>{s.done && <Check className="w-3.5 h-3.5" />}</span>
                    <span className={`flex-1 ${s.done ? "line-through text-slate-500" : "text-white"}`}>{s.subject}{s.paper ? ` · ${s.paper}` : ""}</span><span className="font-mono text-xs text-slate-500">{s.minutes}m</span>
                  </button></li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default function Plan() {
  return (
    <main data-testid="plan-page" className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div><p className="eyebrow">Planner</p><h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none"><W s="Targets and exams" c="Goals and deadlines" /></h1></div>
      <Targets />
      <Exams />
    </main>
  );
}
